import { useMemo, useState } from "react";
import { Download } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/hooks/useAuth";
import {
  useBalancete, useDre, useBalanco,
} from "@/features/contabil/hooks/useContabilizacao";
import { usePlanoContas } from "@/features/contabil/hooks/usePlanoContas";
import { FiltroPeriodo, anoInicio, hoje } from "@/pages/Relatorios/FiltrosRelatorio";

const fmtCur = (v: number) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v);
const fmtNum = (v: number) => v.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// USA-CTB-08: exportação local (os dados já estão na tela; não há endpoint de export).
function exportCsv(filename: string, header: string[], rows: (string | number)[][]) {
  const csv = [header, ...rows]
    .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(";"))
    .join("\n");
  const blob = new Blob([`﻿${csv}`], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

// ── Nível de agregação (sintético/analítico) ─────────────────────────────────
// Agrupa pelo prefixo do código ("3.1.001" no nível 2 → "3.1"). Usa o código, não
// id_conta_mestre, porque o plano importado costuma vir sem a conta mãe preenchida.

type Nivel = "analitico" | "1" | "2" | "3";

const prefixo = (codigo: string | null | undefined, nivel: Nivel) => {
  const c = codigo ?? "";
  if (nivel === "analitico") return c;
  return c.split(".").slice(0, Number(nivel)).join(".");
};

interface LinhaBase { contaId: number; codigoConta?: string | null; descricaoConta?: string | null }

function agrupar<T extends LinhaBase>(
  linhas: T[], nivel: Nivel, descricoes: Map<string, string>,
  somar: (acc: T, l: T) => T,
): T[] {
  if (nivel === "analitico") return linhas;
  const grupos = new Map<string, T>();
  for (const l of linhas) {
    const cod = prefixo(l.codigoConta, nivel);
    const atual = grupos.get(cod);
    if (!atual) {
      const ehPropria = cod === (l.codigoConta ?? "");
      grupos.set(cod, {
        ...l,
        codigoConta: cod,
        descricaoConta: ehPropria ? l.descricaoConta : (descricoes.get(cod) ?? "(grupo)"),
      });
    } else {
      grupos.set(cod, somar(atual, l));
    }
  }
  return [...grupos.values()].sort((a, b) => (a.codigoConta ?? "").localeCompare(b.codigoConta ?? "", "pt-BR", { numeric: true }));
}

function SeletorNivel({ value, onChange }: { value: Nivel; onChange: (v: Nivel) => void }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs text-muted-foreground">Nível</span>
      <Select value={value} onValueChange={(v) => onChange(v as Nivel)}>
        <SelectTrigger className="w-52"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="analitico">Analítico (todas as contas)</SelectItem>
          <SelectItem value="1">Sintético — nível 1</SelectItem>
          <SelectItem value="2">Sintético — nível 2</SelectItem>
          <SelectItem value="3">Sintético — nível 3</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
}

const sufixoNivel = (n: Nivel) => (n === "analitico" ? "analitico" : `nivel${n}`);

export default function RelatoriosContabeisPage() {
  const { user } = useAuth();
  const empresaId = user?.empresa?.id ?? 0;
  const [dtInicio, setDtInicio] = useState(anoInicio());
  const [dtFim, setDtFim] = useState(hoje());
  const [dtPosicao, setDtPosicao] = useState(hoje());
  const [nivelDre, setNivelDre] = useState<Nivel>("analitico");
  const [nivelBalancete, setNivelBalancete] = useState<Nivel>("analitico");
  const [nivelBalanco, setNivelBalanco] = useState<Nivel>("analitico");

  const balancete = useBalancete(empresaId, dtInicio, dtFim);
  const dre = useDre(empresaId, dtInicio, dtFim);
  const balanco = useBalanco(empresaId, dtPosicao);
  const { plano } = usePlanoContas();

  // Descrição das contas sintéticas pelo código.
  const descricoes = useMemo(() => {
    const m = new Map<string, string>();
    for (const c of plano) if (c.codigoPlanoConta && c.descricaoConta) m.set(c.codigoPlanoConta, c.descricaoConta);
    return m;
  }, [plano]);

  const linhasDre = useMemo(() => {
    if (!dre.data) return [];
    // Receita e despesa no mesmo grupo se compensam: soma com sinal e reclassifica.
    const comSinal = dre.data.linhas.map((l) => ({ ...l, valor: l.isReceita ? l.valor : -l.valor }));
    return agrupar(comSinal, nivelDre, descricoes, (a, l) => ({ ...a, valor: a.valor + l.valor }))
      .map((l) => ({ ...l, isReceita: l.valor >= 0, valor: Math.abs(l.valor) }))
      .sort((a, b) => (a.isReceita === b.isReceita ? 0 : a.isReceita ? -1 : 1));
  }, [dre.data, nivelDre, descricoes]);

  const contasBalancete = useMemo(() => !balancete.data ? [] :
    agrupar(balancete.data.contas, nivelBalancete, descricoes, (a, l) => ({
      ...a,
      saldoAnterior: a.saldoAnterior + l.saldoAnterior,
      debitos: a.debitos + l.debitos,
      creditos: a.creditos + l.creditos,
      saldoPeriodo: a.saldoPeriodo + l.saldoPeriodo,
      saldoAtual: a.saldoAtual + l.saldoAtual,
    })), [balancete.data, nivelBalancete, descricoes]);

  const balancoAgrupado = useMemo(() => {
    if (!balanco.data) return null;
    const ag = (ls: typeof balanco.data.ativo) =>
      agrupar(ls, nivelBalanco, descricoes, (a, l) => ({ ...a, saldo: a.saldo + l.saldo }));
    return {
      ativo: ag(balanco.data.ativo),
      passivo: ag(balanco.data.passivo),
      pl: ag(balanco.data.patrimonioLiquido),
    };
  }, [balanco.data, nivelBalanco, descricoes]);

  const exportarDre = () => exportCsv(
    `dre_${dtInicio}_${dtFim}_${sufixoNivel(nivelDre)}.csv`,
    ["Conta", "Descrição", "Tipo", "Valor"],
    [
      ...linhasDre.map((l) => [l.codigoConta ?? "", l.descricaoConta ?? "", l.isReceita ? "Receita" : "Despesa", fmtNum(l.valor)]),
      ["", "Receitas", "", fmtNum(dre.data?.receitas ?? 0)],
      ["", "Despesas", "", fmtNum(dre.data?.despesas ?? 0)],
      ["", "Resultado do período", "", fmtNum(dre.data?.resultadoPeriodo ?? 0)],
    ],
  );

  const exportarBalancete = () => exportCsv(
    `balancete_${dtInicio}_${dtFim}_${sufixoNivel(nivelBalancete)}.csv`,
    ["Conta", "Descrição", "Saldo anterior", "Débitos", "Créditos", "Saldo atual"],
    [
      ...contasBalancete.map((c) => [c.codigoConta ?? "", c.descricaoConta ?? "",
        fmtNum(c.saldoAnterior), fmtNum(c.debitos), fmtNum(c.creditos), fmtNum(c.saldoAtual)]),
      ["", "Total", "", fmtNum(balancete.data?.totalDebitos ?? 0), fmtNum(balancete.data?.totalCreditos ?? 0), ""],
    ],
  );

  const exportarBalanco = () => {
    if (!balancoAgrupado || !balanco.data) return;
    const sec = (nome: string, ls: { codigoConta?: string | null; descricaoConta?: string | null; saldo: number }[]) =>
      ls.map((l) => [nome, l.codigoConta ?? "", l.descricaoConta ?? "", fmtNum(l.saldo)]);
    exportCsv(
      `balanco_${dtPosicao}_${sufixoNivel(nivelBalanco)}.csv`,
      ["Grupo", "Conta", "Descrição", "Saldo"],
      [
        ...sec("Ativo", balancoAgrupado.ativo),
        ...sec("Passivo", balancoAgrupado.passivo),
        ...sec("Patrimônio Líquido", balancoAgrupado.pl),
        ["Total Ativo", "", "", fmtNum(balanco.data.totalAtivo)],
        ["Total Passivo", "", "", fmtNum(balanco.data.totalPassivo)],
        ["Total PL", "", "", fmtNum(balanco.data.totalPatrimonioLiquido)],
      ],
    );
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Relatórios Contábeis</h1>
        <p className="text-muted-foreground">DRE, Balancete e Balanço Patrimonial.</p>
      </div>

      <Tabs defaultValue="dre" className="space-y-4">
        <TabsList>
          <TabsTrigger value="dre">DRE</TabsTrigger>
          <TabsTrigger value="balancete">Balancete</TabsTrigger>
          <TabsTrigger value="balanco">Balanço Patrimonial</TabsTrigger>
        </TabsList>

        <TabsContent value="dre" className="space-y-4">
          <Card>
            <CardContent className="pt-6 flex flex-wrap items-end gap-3">
              <FiltroPeriodo dtInicio={dtInicio} dtFim={dtFim} onDtInicio={setDtInicio} onDtFim={setDtFim} />
              <SeletorNivel value={nivelDre} onChange={setNivelDre} />
              <Button variant="outline" size="sm" className="ml-auto" onClick={exportarDre} disabled={!linhasDre.length}>
                <Download className="h-4 w-4 mr-2" /> Exportar CSV
              </Button>
            </CardContent>
          </Card>

          {dre.isLoading ? <Skeleton className="h-48" /> : !dre.data ? null : (
            <Card>
              <CardHeader>
                <CardTitle>DRE — {new Date(dre.data.dtInicio).toLocaleDateString("pt-BR", { timeZone: "UTC" })} a {new Date(dre.data.dtFim).toLocaleDateString("pt-BR", { timeZone: "UTC" })}</CardTitle>
                <p className="text-sm text-muted-foreground">
                  Receitas: <strong className="text-green-600">{fmtCur(dre.data.receitas)}</strong> ·
                  Despesas: <strong className="text-red-600">{fmtCur(dre.data.despesas)}</strong> ·
                  Resultado: <strong className={dre.data.resultadoPeriodo >= 0 ? "text-green-600" : "text-red-600"}>{fmtCur(dre.data.resultadoPeriodo)}</strong>
                </p>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Conta</TableHead>
                      <TableHead>Descrição</TableHead>
                      <TableHead>Tipo</TableHead>
                      <TableHead className="text-right">Valor</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {linhasDre.length === 0 ? (
                      <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground">Sem movimento de resultado no período.</TableCell></TableRow>
                    ) : linhasDre.map((l) => (
                      <TableRow key={l.codigoConta ?? l.contaId}>
                        <TableCell className="font-mono text-sm">{l.codigoConta}</TableCell>
                        <TableCell>{l.descricaoConta}</TableCell>
                        <TableCell>{l.isReceita ? "Receita" : "Despesa"}</TableCell>
                        <TableCell className={`text-right font-semibold ${l.isReceita ? "text-green-600" : "text-red-600"}`}>
                          {fmtCur(l.valor)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="balancete" className="space-y-4">
          <Card>
            <CardContent className="pt-6 flex flex-wrap items-end gap-3">
              <FiltroPeriodo dtInicio={dtInicio} dtFim={dtFim} onDtInicio={setDtInicio} onDtFim={setDtFim} />
              <SeletorNivel value={nivelBalancete} onChange={setNivelBalancete} />
              <Button variant="outline" size="sm" className="ml-auto" onClick={exportarBalancete} disabled={!contasBalancete.length}>
                <Download className="h-4 w-4 mr-2" /> Exportar CSV
              </Button>
            </CardContent>
          </Card>

          {balancete.isLoading ? <Skeleton className="h-48" /> : !balancete.data ? null : (
            <Card>
              <CardHeader>
                <CardTitle>Balancete — {contasBalancete.length} contas</CardTitle>
                <p className="text-sm text-muted-foreground">
                  Total D: <strong>{fmtCur(balancete.data.totalDebitos)}</strong> ·
                  Total C: <strong>{fmtCur(balancete.data.totalCreditos)}</strong>
                </p>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Conta</TableHead>
                      <TableHead>Descrição</TableHead>
                      <TableHead className="text-right">Saldo anterior</TableHead>
                      <TableHead className="text-right">Débitos</TableHead>
                      <TableHead className="text-right">Créditos</TableHead>
                      <TableHead className="text-right">Saldo atual</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {contasBalancete.map((c) => (
                      <TableRow key={c.codigoConta ?? c.contaId}>
                        <TableCell className="font-mono text-sm">{c.codigoConta}</TableCell>
                        <TableCell>{c.descricaoConta}</TableCell>
                        <TableCell className="text-right">{fmtCur(c.saldoAnterior)}</TableCell>
                        <TableCell className="text-right">{fmtCur(c.debitos)}</TableCell>
                        <TableCell className="text-right">{fmtCur(c.creditos)}</TableCell>
                        <TableCell className={`text-right font-semibold ${c.saldoAtual >= 0 ? "" : "text-red-600"}`}>
                          {fmtCur(c.saldoAtual)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="balanco" className="space-y-4">
          <Card>
            <CardContent className="pt-6 flex flex-wrap items-end gap-3">
              <div className="flex flex-col gap-1">
                <Label className="text-xs text-muted-foreground font-normal">Data da posição</Label>
                <Input type="date" value={dtPosicao} onChange={(e) => setDtPosicao(e.target.value)} className="w-40" />
              </div>
              <SeletorNivel value={nivelBalanco} onChange={setNivelBalanco} />
              <Button variant="outline" size="sm" className="ml-auto" onClick={exportarBalanco} disabled={!balancoAgrupado}>
                <Download className="h-4 w-4 mr-2" /> Exportar CSV
              </Button>
            </CardContent>
          </Card>

          {balanco.isLoading ? <Skeleton className="h-48" /> : !balanco.data || !balancoAgrupado ? null : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Card>
                <CardHeader>
                  <CardTitle>Ativo</CardTitle>
                  <p className="text-sm text-muted-foreground">Total: <strong>{fmtCur(balanco.data.totalAtivo)}</strong></p>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader><TableRow><TableHead>Conta</TableHead><TableHead className="text-right">Saldo</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {balancoAgrupado.ativo.map((l) => (
                        <TableRow key={l.codigoConta ?? l.contaId}>
                          <TableCell className="text-sm">{l.codigoConta} — {l.descricaoConta}</TableCell>
                          <TableCell className="text-right">{fmtCur(l.saldo)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle>Passivo + PL</CardTitle>
                  <p className="text-sm text-muted-foreground">
                    Passivo: <strong>{fmtCur(balanco.data.totalPassivo)}</strong> ·
                    PL: <strong>{fmtCur(balanco.data.totalPatrimonioLiquido)}</strong>
                  </p>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader><TableRow><TableHead>Conta</TableHead><TableHead className="text-right">Saldo</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {[...balancoAgrupado.passivo, ...balancoAgrupado.pl].map((l) => (
                        <TableRow key={`${l.codigoConta ?? ""}-${l.contaId}`}>
                          <TableCell className="text-sm">{l.codigoConta} — {l.descricaoConta}</TableCell>
                          <TableCell className="text-right">{fmtCur(l.saldo)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
